
import { useEffect, useState } from 'react';
import Link from '@/components/compat/Link';
import { ExternalLink, RotateCcw, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import { usePainelText } from '@/components/painel/painel-texts-context';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelButtonResetRingClass,
  painelDescriptionClass,
  painelSurfaceClass,
  painelSurfaceAccentClass,
  painelSwitchClass,
} from '@/components/painel/painel-styles';
import {
  COMING_SOON_DEFAULTS,
  syncComingSoonCookie,
  type ComingSoonConfig,
} from '@/lib/coming-soon';
import {
  getComingSoonConfig,
  updateComingSoonConfig,
} from '@/services/coming-soon.service';
import { cn } from '@/lib/utils';

function clampProgress(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function PainelComingSoonPage() {
  const pageText = usePainelText('coming_soon');
  const [hydrated, setHydrated] = useState(false);
  const [form, setForm] = useState<ComingSoonConfig>(COMING_SOON_DEFAULTS);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void getComingSoonConfig().then((cfg) => {
      if (cancelled) return;
      const next = cfg ?? COMING_SOON_DEFAULTS;
      setForm(next);
      syncComingSoonCookie(next.enabled);
      setHydrated(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const next = await updateComingSoonConfig(form);
      setForm(next);
      setDirty(false);
      syncComingSoonCookie(next.enabled);
      toast.success(
        next.enabled
          ? 'Modo Coming Soon activado — visitantes serão redireccionados.'
          : 'Alterações guardadas.',
        {
          description: 'Configuração guardada no servidor.',
        }
      );
    } catch {
      toast.error('Não foi possível guardar a configuração.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ ...COMING_SOON_DEFAULTS });
    setDirty(true);
    toast.message('Formulário reposto aos valores padrão. Guarde para aplicar.');
  };

  function patchForm(patch: Partial<ComingSoonConfig>) {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
            System
          </p>
          <h1 className="mt-1 font-poppins text-2xl font-bold text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className={cn('mt-2 max-w-2xl text-sm', painelDescriptionClass)}>
            {pageText.description}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          asChild
          className={cn(painelButtonOutlineClass, painelButtonResetRingClass, 'h-10 shrink-0')}
        >
          <Link href="/coming-soon" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            Pré-visualizar
          </Link>
        </Button>
      </div>

      <div
        className={cn(
          painelSurfaceAccentClass,
          'flex flex-wrap items-center gap-4 px-5 py-4 sm:px-6',
          !hydrated && 'pointer-events-none opacity-70'
        )}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/25">
          <Sparkles className="h-5 w-5 text-sky-300" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-poppins text-sm font-semibold text-white">Modo Coming Soon</p>
          <p className="font-inter text-xs text-slate-400">
            Quando activo, visitantes são redireccionados para{' '}
            <span className="text-slate-300">/coming-soon</span>. O painel e o login administrativo
            permanecem acessíveis.
          </p>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(checked) => patchForm({ enabled: checked })}
          className={cn(painelSwitchClass, 'shrink-0')}
        />
      </div>

      <div
        className={cn(
          'grid gap-6 lg:grid-cols-2',
          !hydrated && 'pointer-events-none opacity-70'
        )}
      >
        <div className={cn(painelSurfaceClass, 'space-y-5 p-5 md:p-6')}>
          <h2 className="font-poppins text-sm font-semibold text-white">Conteúdo da página</h2>

          <div className="space-y-2">
            <Label className="text-slate-300">Título principal</Label>
            <input
              value={form.title}
              onChange={(e) => patchForm({ title: e.target.value })}
              className={painelInputClass}
              placeholder="O futuro começa agora"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Descrição</Label>
            <RichTextEditor
              value={form.description}
              onChange={(description) => patchForm({ description })}
              toolbar="standard"
              editorMinHeightClass="min-h-[160px]"
              resetKey={hydrated ? 'coming-soon-desc' : 'loading'}
            />
            <p className="font-inter text-xs text-slate-500">
              Use parágrafos e listas na toolbar. Para manter &quot;xat Ajuda&quot; na mesma linha,
              seleccione o texto e evite quebras no meio da marca.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Previsão de lançamento (opcional)</Label>
            <input
              value={form.estimatedLaunch}
              onChange={(e) => patchForm({ estimatedLaunch: e.target.value })}
              className={painelInputClass}
              placeholder="Ex.: Lançamento previsto para o segundo semestre de 2026"
            />
          </div>
        </div>

        <div className={cn(painelSurfaceClass, 'space-y-5 p-5 md:p-6')}>
          <h2 className="font-poppins text-sm font-semibold text-white">Barra de progresso</h2>

          <div className="space-y-2">
            <Label className="text-slate-300">Estado do desenvolvimento</Label>
            <input
              value={form.progressStatus}
              onChange={(e) => patchForm({ progressStatus: e.target.value })}
              className={painelInputClass}
              placeholder="Ajustes finais e testes internos"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Progresso da plataforma</Label>
              <span className="font-poppins text-sm font-bold text-sky-200">
                {clampProgress(form.progress)}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={clampProgress(form.progress)}
              onChange={(e) => patchForm({ progress: Number(e.target.value) })}
              className="w-full accent-sky-500"
            />
            <div className="h-2.5 w-full overflow-hidden rounded-full border border-white/10 bg-white/[0.04]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-600 via-sky-400 to-cyan-300 transition-[width] duration-300"
                style={{ width: `${clampProgress(form.progress)}%` }}
              />
            </div>
            <p className="font-inter text-xs text-slate-500">
              Valor exibido na barra animada da página pública.
            </p>
          </div>

          <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 font-inter text-xs leading-relaxed text-amber-100/90">
            Para activar em produção sem depender deste navegador, defina a variável de ambiente{' '}
            <code className="rounded bg-black/20 px-1 py-0.5 text-amber-50">
              COMING_SOON_ENABLED=true
            </code>{' '}
            no deploy.
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="ghost"
          disabled={!dirty}
          className={cn(painelButtonOutlineClass, painelButtonResetRingClass, 'h-11')}
          onClick={handleReset}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Repor padrões
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={saving || !dirty}
          className={cn(painelButtonPrimaryClass, painelButtonResetRingClass, 'h-11 px-6')}
          onClick={() => void handleSave()}
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'A guardar…' : 'Guardar alterações'}
        </Button>
      </div>
    </div>
  );
}
